'use client';

/* eslint-disable react-refresh/only-export-components -- provider and hook are co-located by design */
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import type { Store } from '@/lib/api/stores.server';

export type StoreIdAnalyticsLoaderData = {
    restaurantAnalyticsName?: string | null;
    storeAnalyticsName?: string | null;
};

type LocationPageContextValue = {
    store: Store | null;
    locationName: string | null;
    parentLocationLabel: string | null;
    restaurantAnalyticsName: string | null;
    storeAnalyticsName: string | null;
};

const LocationPageContext = createContext<LocationPageContextValue>({
    store: null,
    locationName: null,
    parentLocationLabel: null,
    restaurantAnalyticsName: null,
    storeAnalyticsName: null,
});

export function LocationStoreProvider({
    store,
    locationName = null,
    parentLocationLabel = null,
    restaurantAnalyticsName = null,
    storeAnalyticsName = null,
    children,
}: {
    store: Store | null;
    locationName?: string | null;
    parentLocationLabel?: string | null;
    restaurantAnalyticsName?: string | null;
    storeAnalyticsName?: string | null;
    children: ReactNode;
}): ReactElement {
    return (
        <LocationPageContext.Provider
            value={{
                store,
                locationName: locationName?.trim() || null,
                parentLocationLabel: parentLocationLabel?.trim() || null,
                restaurantAnalyticsName: restaurantAnalyticsName?.trim() || null,
                storeAnalyticsName: storeAnalyticsName?.trim() || null,
            }}>
            {children}
        </LocationPageContext.Provider>
    );
}

export function useLocationStore(): Store | null {
    return useContext(LocationPageContext).store;
}

/** Page Designer location label for in-page analytics (e.g. flyer_click on a store page). */
export function usePageLocationName(): string | null {
    return useContext(LocationPageContext).locationName;
}

/** GA4 `restaurant_name` from storeId (loader data overrides page context). */
export function useRestaurantAnalyticsName(loaderData?: StoreIdAnalyticsLoaderData | null): string | undefined {
    const fromContext = useContext(LocationPageContext).restaurantAnalyticsName?.trim();
    const fromLoader = loaderData?.restaurantAnalyticsName?.trim();
    return fromLoader || fromContext || undefined;
}

/** GA4 `store_name` from storeId (loader data overrides page context). */
export function useStoreAnalyticsName(loaderData?: StoreIdAnalyticsLoaderData | null): string | undefined {
    const fromContext = useContext(LocationPageContext).storeAnalyticsName?.trim();
    const fromLoader = loaderData?.storeAnalyticsName?.trim();
    return fromLoader || fromContext || undefined;
}
